import { ErrorLifecycle, FVError, FVErrorCode } from 'js/file_viewer/core/types/errors';
import { PreviewType } from 'js/file_viewer/core/types/preview_type';
import { fileSizeSupported } from 'js/file_viewer/core/utils/config';
import { getFvErrorCodeFromPreviewDataApi } from 'js/file_viewer/core/utils/errors';
import { resolvePreviewTypeFromExtension } from 'js/file_viewer/core/utils/extension_constants';
import { getFileExtension } from 'js/file_viewer/core/utils/paths';
import { GOOGLE_CLOUD_DOC_EXTS } from 'js/file_viewer/file_extension/extension_constants';
export const DEFAULT_SUPPORTED_PREVIEW_TYPES = [
    PreviewType.CloudDoc,
    PreviewType.Image,
    PreviewType.SsrDoc,
    PreviewType.Video,
    PreviewType.Audio,
    PreviewType.Archive,
    PreviewType.Excel,
    PreviewType.Text,
    PreviewType.RawHTML,
    PreviewType.Linkfile,
];
export function resolvePreviewType(data, filename) {
    if (!data || !data.content) {
        return filename
            ? resolvePreviewTypeFromExtension(getFileExtension(filename))
            : PreviewType.Error;
    }
    switch (data.content['.tag']) {
        case 'archive':
            return PreviewType.Archive;
        case 'audio':
            return PreviewType.Audio;
        case 'cloud_doc':
            return PreviewType.CloudDoc;
        case 'excel':
            return PreviewType.Excel;
        case 'text':
            return PreviewType.Text;
        case 'image':
            return PreviewType.Image;
        case 'linkfile':
            return PreviewType.Linkfile;
        case 'raw_html':
            return PreviewType.RawHTML;
        case 'restricted':
            return PreviewType.Restricted;
        case 'ssr_doc':
            return PreviewType.SsrDoc;
        case 'video':
            return PreviewType.Video;
        default:
            return PreviewType.Other;
    }
}
const isOnline = () => {
    var _a;
    if (navigator.onLine) {
        return true;
    }
    // navigator.onLine can return a false negative if the user hits a rare macOS Chrome bug:
    // https://bugs.chromium.org/p/chromium/issues/detail?id=678075. In that case, we do a secondary check on
    // navigation.connection.rtt. navigation.connection is only available in Chromium browsers, but the bug that
    // necessitates this workaround only affects Chrome. If a non-Chromium browser is offline, the truthy check on
    // navigator.connection will fail and we'll correctly return false.
    const navigatorWithConnection = navigator;
    return ((_a = navigatorWithConnection.connection) === null || _a === void 0 ? void 0 : _a.rtt) && navigatorWithConnection.connection.rtt > 0;
};
export const validatePreviewMetadata = (previewMetadata, serverError, localError, filename, fileSize, config) => {
    var _a;
    if (localError) {
        // localErrors are prioritized over server errors since they are set explicitly by the client.
        return { error: localError, previewType: PreviewType.Error };
    }
    const previewType = resolvePreviewType(previewMetadata, filename);
    // Grab extension from file name or metadata (if not provided in file name)
    let fileExtension = getFileExtension(filename);
    if (!fileExtension) {
        fileExtension = (_a = previewMetadata === null || previewMetadata === void 0 ? void 0 : previewMetadata.file_type_extension) !== null && _a !== void 0 ? _a : '';
    }
    const convertedServerError = serverError
        ? new FVError(getFvErrorCodeFromPreviewDataApi(serverError), ErrorLifecycle.Support, `Got error from preview api: ${serverError['.tag']}`)
        : undefined;
    let reconciledError = convertedServerError;
    if (serverError && (convertedServerError === null || convertedServerError === void 0 ? void 0 : convertedServerError.type) === FVErrorCode.FileEncrypted) {
        // Prioritize file encrypted error over missing file extension error.
        // Do nothing and keep the error the same.
    }
    else if (!fileExtension) {
        reconciledError = new FVError(FVErrorCode.NoExtension, ErrorLifecycle.Support, 'Missing file extension');
    }
    else if (!isOnline()) {
        reconciledError = new FVError(FVErrorCode.UserOffline, ErrorLifecycle.Support, 'There is a network connectivity issue');
    }
    else if (!DEFAULT_SUPPORTED_PREVIEW_TYPES.includes(previewType) ||
        GOOGLE_CLOUD_DOC_EXTS.includes(fileExtension)) {
        reconciledError = new FVError(FVErrorCode.UnsupportedPreviewType, ErrorLifecycle.Support, 'Unsupported preview type');
    }
    else if (config &&
        filename &&
        fileSize &&
        !fileSizeSupported(fileExtension, fileSize, previewType, config)) {
        reconciledError = new FVError(FVErrorCode.FileTooLarge, ErrorLifecycle.Support, 'Unsupported file size');
    }
    else if (!previewMetadata && !serverError) {
        reconciledError = new FVError(FVErrorCode.MissingContentMetadata, ErrorLifecycle.Support, 'Missing data');
    }
    return {
        ...previewMetadata,
        previewType: reconciledError ? PreviewType.Error : previewType,
        error: reconciledError,
    };
};
// Prepares file info for 'get_preview_data_batch' RPC
export const prepareFileInfo = (fileInfo, sharedLinkInfo) => {
    var _a;
    let file;
    const url = (_a = fileInfo === null || fileInfo === void 0 ? void 0 : fileInfo.url) !== null && _a !== void 0 ? _a : sharedLinkInfo === null || sharedLinkInfo === void 0 ? void 0 : sharedLinkInfo.url;
    if (url) {
        // if we have both a URL and NsId + SjId, but fetch by the revision information,
        // the return value has no file ID on it (for some reason)
        // so prioritize fetching by URL if possible.
        file = { url: url, file_id: fileInfo === null || fileInfo === void 0 ? void 0 : fileInfo.file_id };
    }
    else if ((fileInfo === null || fileInfo === void 0 ? void 0 : fileInfo.ns_id) && fileInfo.sj_id !== undefined) {
        // If a FileId is included, get_preview_data_batch fetches the latest version only
        // by removing the FileId we get the revision we're interested in.
        file = { ns_id: fileInfo.ns_id, sj_id: fileInfo.sj_id, file_id: fileInfo.file_id };
    }
    else {
        file = { file_id: fileInfo === null || fileInfo === void 0 ? void 0 : fileInfo.file_id };
    }
    return file;
};
// Prepares file info for 'get_archive_files' and 'logged_out/get_archive_files' RPCs
export const prepareArchiveQueryFileInfo = (fileInfo, sharedLinkInfo) => ({
    ns_id: fileInfo === null || fileInfo === void 0 ? void 0 : fileInfo.ns_id,
    sj_id: fileInfo === null || fileInfo === void 0 ? void 0 : fileInfo.sj_id,
    file_id: fileInfo === null || fileInfo === void 0 ? void 0 : fileInfo.file_id,
    url: (fileInfo === null || fileInfo === void 0 ? void 0 : fileInfo.url) || (sharedLinkInfo === null || sharedLinkInfo === void 0 ? void 0 : sharedLinkInfo.url),
});
export const createNestedArchivePreviewMetadataQueryKey = (fileInfo, subpaths) => {
    return [
        'custom',
        'previews:get_archive_files',
        { root_file_info: fileInfo, subpaths, subpath_sizes: undefined },
    ];
};
//# sourceMappingURL=support.preenvesnextjs.map
